import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  addAffiliateCode,
  editAffiliateCode,
  getAffiliateCodeById,
} from "../../../service/api";
import { decryptString } from "../../../config/validation";

const AffiliateForm = ({ onBack, editId = null }) => {
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editId) fetchAffiliate();
  }, [editId]);

  const fetchAffiliate = async () => {
    setFetchLoading(true);
    try {
      const res = await getAffiliateCodeById(editId);
      if (res?.status && res?.data) {
        setCode(res.data.code || "");
        setName(res.data.name || "");
        setRedirectUrl(res.data.redirectUrl || "");
        setIsActive(res.data.isActive !== false);
      } else {
        toast.error("Failed to load affiliate code details");
        onBack();
      }
    } catch {
      toast.error("Something went wrong");
      onBack();
    }
    setFetchLoading(false);
  };

  const validate = () => {
    const errs = {};
    if (!editId) {
      if (!code.trim()) {
        errs.code = "Code is required";
      } else if (!/^[A-Z0-9]+$/.test(code.trim())) {
        errs.code = "Letters and numbers only";
      } else if (code.trim().length < 3) {
        errs.code = "Minimum 3 characters";
      } else if (code.trim().length > 20) {
        errs.code = "Maximum 20 characters";
      }
    }
    if (!name.trim()) {
      errs.name = "Name is required";
    } else if (name.trim().length > 100) {
      errs.name = "Maximum 100 characters";
    }
    if (redirectUrl.trim()) {
      try {
        new URL(redirectUrl.trim());
      } catch {
        errs.redirectUrl = "Enter a valid URL (e.g. https://example.com)";
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      let res;
      if (editId) {
        const encrypted = await decryptString({
          name: name.trim(),
          isActive,
          redirectUrl: redirectUrl.trim() || null,
        });
        res = await editAffiliateCode(editId, { encrypt: encrypted });
      } else {
        const encrypted = await decryptString({
          code: code.trim().toUpperCase(),
          name: name.trim(),
          redirectUrl: redirectUrl.trim() || null,
        });
        res = await addAffiliateCode({ encrypt: encrypted });
      }
      if (res?.status) {
        toast.success(
          res?.message ||
            `Affiliate code ${editId ? "updated" : "created"} successfully`,
        );
        onBack();
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch (e) {
      toast.error(e?.data?.message || "Something went wrong");
    }
    setLoading(false);
  };

  if (fetchLoading) {
    return (
      <div className="content container-fluid text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="content container-fluid">

      {/* Page header */}
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">
              {editId ? "Edit Affiliate Code" : "Create Affiliate Code"}
            </h3>
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/admin">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <a
                  role="button"
                  style={{ cursor: "pointer" }}
                  onClick={onBack}
                >
                  Affiliate Codes
                </a>
              </li>
              <li className="breadcrumb-item active">
                {editId ? "Edit" : "Create"}
              </li>
            </ul>
          </div>
          <div className="col-auto">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onBack}
            >
              <i className="fe fe-arrow-left me-1" />
              Back
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-7 col-lg-8 col-md-10">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                {editId ? "Update affiliate details" : "New affiliate code"}
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} noValidate>

                {/* Code field */}
                <div className="form-group mb-3">
                  <label className="form-label">
                    Code{" "}
                    {!editId && <span className="text-danger">*</span>}
                  </label>
                  {editId ? (
                    <>
                      <input
                        type="text"
                        className="form-control"
                        value={code}
                        disabled
                        style={{ fontFamily: "monospace", fontWeight: 600 }}
                      />
                      <small className="text-muted">
                        Code cannot be changed after creation
                      </small>
                    </>
                  ) : (
                    <>
                      <input
                        type="text"
                        className={`form-control ${errors.code ? "is-invalid" : ""}`}
                        value={code}
                        onChange={(e) =>
                          setCode(
                            e.target.value
                              .toUpperCase()
                              .replace(/[^A-Z0-9]/g, ""),
                          )
                        }
                        placeholder="e.g. PARTNER2024"
                        maxLength={20}
                        style={{ fontFamily: "monospace", fontWeight: 600 }}
                      />
                      {errors.code ? (
                        <div className="invalid-feedback">{errors.code}</div>
                      ) : (
                        <small className="text-muted">
                          Letters and numbers only · 3–20 characters
                        </small>
                      )}
                    </>
                  )}
                </div>

                {/* Name field */}
                <div className="form-group mb-3">
                  <label className="form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Affiliate partner or campaign name"
                    maxLength={100}
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>

                {/* Redirect URL */}
                <div className="form-group mb-3">
                  <label className="form-label">Redirect URL</label>
                  <input
                    type="url"
                    className={`form-control ${errors.redirectUrl ? "is-invalid" : ""}`}
                    value={redirectUrl}
                    onChange={(e) => setRedirectUrl(e.target.value)}
                    placeholder="https://example.com/landing"
                    maxLength={500}
                  />
                  {errors.redirectUrl ? (
                    <div className="invalid-feedback">{errors.redirectUrl}</div>
                  ) : (
                    <small className="text-muted">
                      Optional — overrides the default landing page for this
                      code
                    </small>
                  )}
                </div>

                {/* Status toggle — edit only */}
                {editId && (
                  <div className="form-group mb-4">
                    <label className="form-label d-block">Status</label>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <span
                          style={{
                            fontWeight: 600,
                            color: isActive ? "#28a745" : "#dc3545",
                          }}
                        >
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      }
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="d-flex gap-2 pt-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onBack}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                    style={{ minWidth: 120 }}
                  >
                    {loading ? (
                      <Spinner size="sm" />
                    ) : editId ? (
                      "Save Changes"
                    ) : (
                      "Create Code"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateForm;
